import { FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import React from 'react';

import { OsTabs } from 'components/atoms/OsTabs';
import { OperatingSystem } from './cliInstructionTypes';
import { CliCommandBlock } from './CopyableCliBlock';
import { StepHelp } from './StepHelp';
import { getTerminalOpenGuidance } from './useCliInstructionSteps';

export type AwsProfileChoice = 'default' | 'named';

export const AWS_PROFILE_NAME_PATTERN = /^[a-zA-Z0-9_.-]+$/;
export const AWS_PROFILE_NAME_ERROR = 'Use letters, numbers, periods, underscores, and hyphens.';

export const isValidAwsProfileName = (awsProfileName: string) => AWS_PROFILE_NAME_PATTERN.test(awsProfileName.trim());

export const isAwsProfileChoiceReady = (profileChoice: AwsProfileChoice | null, awsProfileName: string) =>
  profileChoice === 'default' || (profileChoice === 'named' && isValidAwsProfileName(awsProfileName));

export const AwsProfileSelectionStep = ({
  awsProfileName,
  profileChoice,
  selectedOs,
  setAwsProfileName,
  setProfileChoice,
  setSelectedOs,
}: {
  awsProfileName: string;
  profileChoice: AwsProfileChoice | null;
  selectedOs: OperatingSystem;
  setAwsProfileName: (awsProfileName: string) => void;
  setProfileChoice: (profileChoice: AwsProfileChoice) => void;
  setSelectedOs: (operatingSystem: OperatingSystem) => void;
}) => (
  <Stack spacing={1.5}>
    <Typography variant="body2" color="text.secondary">
      List the profiles on this computer, then choose the one this upload will use.
    </Typography>
    <StepHelp
      items={[
        {
          question: 'What is a profile?',
          answer:
            'A saved name for one set of AWS credentials on this computer. Default means you do not type --profile.',
        },
        {
          question: 'The list prints default.',
          answer: 'Choose Default. Do not type default into the named profile field.',
        },
      ]}
    />
    <OsTabs
      ariaLabel="AWS profile instructions by operating system"
      selectedOs={selectedOs}
      setSelectedOs={setSelectedOs}
    />
    <Typography variant="body2" color="text.secondary">
      {getTerminalOpenGuidance(selectedOs)}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Run this command. It prints the profile names saved on this computer.
    </Typography>
    <CliCommandBlock command="aws configure list-profiles" />
    <Typography variant="body2" color="text.secondary">
      If the list is empty, or you do not normally type --profile, choose Default. Otherwise copy one name from the
      list.
    </Typography>
    <RadioGroup
      aria-label="AWS profile to use"
      value={profileChoice || ''}
      onChange={(event) => {
        const nextChoice = event.target.value as AwsProfileChoice;
        setProfileChoice(nextChoice);
        if (nextChoice === 'default') {
          setAwsProfileName('');
        }
      }}
    >
      <FormControlLabel value="default" control={<Radio />} label="Default (no profile name)" />
      <FormControlLabel value="named" control={<Radio />} label="Named profile" />
    </RadioGroup>
    {profileChoice === 'named' ? (
      <TextField
        label="AWS profile name"
        value={awsProfileName}
        onChange={(event) => setAwsProfileName(event.target.value)}
        fullWidth
        placeholder="my-upload-profile"
        error={Boolean(awsProfileName.trim()) && !isValidAwsProfileName(awsProfileName)}
        helperText={
          Boolean(awsProfileName.trim()) && !isValidAwsProfileName(awsProfileName)
            ? AWS_PROFILE_NAME_ERROR
            : 'Copy a name from the list above.'
        }
      />
    ) : null}
  </Stack>
);
